import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { KoFiWidget } from './components/KoFiWidget';
import { FeedbackTrigger } from './components/FeedbackTrigger';
import { useApiClient } from './lib/api-client';
import { useTheme } from './lib/theme';

// Lazy-loaded routes — each gets its own chunk, only fetched when first visited.
const SignIn        = lazy(() => import('./routes/SignIn').then((m) => ({ default: m.SignIn })));
const Eula         = lazy(() => import('./routes/Eula').then((m) => ({ default: m.Eula })));
const Systems      = lazy(() => import('./routes/Systems').then((m) => ({ default: m.Systems })));
const AddSystem    = lazy(() => import('./routes/AddSystem').then((m) => ({ default: m.AddSystem })));
const Recommend    = lazy(() => import('./routes/Recommend').then((m) => ({ default: m.Recommend })));
const Connect      = lazy(() => import('./routes/Connect').then((m) => ({ default: m.Connect })));
const EditSystem   = lazy(() => import('./routes/EditSystem').then((m) => ({ default: m.EditSystem })));
const ManageModels = lazy(() => import('./routes/ManageModels').then((m) => ({ default: m.ManageModels })));
const Dashboard    = lazy(() => import('./routes/Dashboard').then((m) => ({ default: m.Dashboard })));
const Feedback     = lazy(() => import('./routes/Feedback').then((m) => ({ default: m.Feedback })));
const Prompts      = lazy(() => import('./routes/Prompts').then((m) => ({ default: m.Prompts })));
const Agents       = lazy(() => import('./routes/Agents').then((m) => ({ default: m.Agents })));
const AgentDetail  = lazy(() => import('./routes/AgentDetail').then((m) => ({ default: m.AgentDetail })));
const AgentEditor  = lazy(() => import('./routes/AgentEditor').then((m) => ({ default: m.AgentEditor })));
const Mcp          = lazy(() => import('./routes/Mcp').then((m) => ({ default: m.Mcp })));
const Approvals    = lazy(() => import('./routes/Approvals').then((m) => ({ default: m.Approvals })));
const Cluster      = lazy(() => import('./routes/Cluster').then((m) => ({ default: m.Cluster })));
const Notebooks    = lazy(() => import('./routes/Notebooks').then((m) => ({ default: m.Notebooks })));
const Simulator    = lazy(() => import('./routes/Simulator').then((m) => ({ default: m.Simulator })));
const SimulatorCatalog = lazy(() => import('./routes/SimulatorCatalog').then((m) => ({ default: m.SimulatorCatalog })));
const AdminEula    = lazy(() => import('./routes/AdminEula').then((m) => ({ default: m.AdminEula })));
const AdminFeedback   = lazy(() => import('./routes/AdminFeedback').then((m) => ({ default: m.AdminFeedback })));
const AdminKofi       = lazy(() => import('./routes/AdminKofi').then((m) => ({ default: m.AdminKofi })));
const AdminUsers      = lazy(() => import('./routes/AdminUsers').then((m) => ({ default: m.AdminUsers })));
const AdminAffiliate  = lazy(() => import('./routes/AdminAffiliate').then((m) => ({ default: m.AdminAffiliate })));

function PageSpinner() {
  return (
    <main className="container page" aria-busy="true">
      <p className="body" style={{ opacity: 0.6 }}>Loading…</p>
    </main>
  );
}

function EulaGate({ children }: { children: ReactNode }) {
  const client = useApiClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<'checking' | 'accepted' | 'pending'>('checking');

  useEffect(() => {
    if (!client.isLoaded) return;
    let cancelled = false;
    client
      .get<{ version: string; accepted: boolean }>('/api/eula/acceptance')
      .then((r) => {
        if (!cancelled) setStatus(r.accepted ? 'accepted' : 'pending');
      })
      .catch(() => {
        if (!cancelled) setStatus('pending');
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  useEffect(() => {
    if (status === 'pending' && location.pathname !== '/eula') {
      navigate('/eula', { replace: true });
    }
  }, [status, location.pathname, navigate]);

  if (status === 'checking') {
    return (
      <main className="container page" aria-busy="true">
        <p className="body" style={{ opacity: 0.6 }}>Loading…</p>
      </main>
    );
  }
  return <>{children}</>;
}

/** Thin wrapper so every page-level route stays DRY. */
function Protected({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute>
      <EulaGate>{children}</EulaGate>
    </ProtectedRoute>
  );
}

export default function App() {
  useTheme();
  const isDev = (import.meta.env.VITE_CLERK_PUBLISHABLE_KEY ?? '').startsWith('pk_test_');

  return (
    <>
      <div className="gw-bg-stage" />
      <div className="gw-app">
        <Header />
        <main style={{ flex: 1 }}>
          <Suspense fallback={<PageSpinner />}>
          <Routes>
            {/* Auth */}
            <Route path="/sign-in/*" element={<SignIn kind="sign-in" />} />
            <Route path="/sign-up/*" element={<SignIn kind="sign-up" />} />

            {/* Root → systems */}
            <Route path="/" element={<Protected><Navigate to="/systems" replace /></Protected>} />

            {/* EULA (protected but not EulaGated — it IS the gate) */}
            <Route path="/eula" element={<ProtectedRoute><Eula /></ProtectedRoute>} />

            {/* Main app */}
            <Route path="/systems"               element={<Protected><Systems /></Protected>} />
            <Route path="/systems/add"           element={<Protected><AddSystem /></Protected>} />
            <Route path="/systems/:id/recommend" element={<Protected><Recommend /></Protected>} />
            <Route path="/systems/:id/connect"   element={<Protected><Connect /></Protected>} />
            <Route path="/systems/:id/edit"     element={<Protected><EditSystem /></Protected>} />
            <Route path="/systems/:id/models"   element={<Protected><ManageModels /></Protected>} />
            <Route path="/dashboard"             element={<Protected><Dashboard /></Protected>} />
            <Route path="/prompts"               element={<Protected><Prompts /></Protected>} />
            <Route path="/agents"               element={<Protected><Agents /></Protected>} />
            <Route path="/agents/new"           element={<Protected><AgentEditor /></Protected>} />
            <Route path="/agents/:id"           element={<Protected><AgentDetail /></Protected>} />
            <Route path="/agents/:id/edit"      element={<Protected><AgentEditor /></Protected>} />
            <Route path="/mcp"                   element={<Protected><Mcp /></Protected>} />
            <Route path="/approvals"             element={<Protected><Approvals /></Protected>} />
            <Route path="/cluster"               element={<Protected><Cluster /></Protected>} />
            <Route path="/notebooks"             element={<Protected><Notebooks /></Protected>} />
            <Route path="/simulator"             element={<Protected><SimulatorCatalog /></Protected>} />
            <Route path="/simulator/:id"         element={<Protected><Simulator /></Protected>} />
            <Route path="/feedback"              element={<Protected><Feedback /></Protected>} />

            {/* Admin */}
            <Route path="/admin/eula"     element={<Protected><AdminEula /></Protected>} />
            <Route path="/admin/feedback" element={<Protected><AdminFeedback /></Protected>} />
            <Route path="/admin/kofi"     element={<Protected><AdminKofi /></Protected>} />
            <Route path="/admin/users"       element={<Protected><AdminUsers /></Protected>} />
            <Route path="/admin/affiliate"   element={<Protected><AdminAffiliate /></Protected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </main>
        <Footer dev={isDev} />
        {/* Floating overlays — outside the route tree so they persist across navigation */}
        <KoFiWidget />
        <FeedbackTrigger />
      </div>
    </>
  );
}
